type DateLike = number | string | Date | null | undefined;

export function dateToFullDateString(date: Date, localeCode: string): string {
    return date.toLocaleString(localeCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}

/**
 * Parses a birthdate value (stored as timestamp or YYYY-MM-DD) into a Date
 * representing that calendar day for consistent display and age calculation.
 * - Timestamp: interpreted as UTC midnight (so the same calendar day everywhere).
 * - YYYY-MM-DD string: parsed as local date.
 *
 * SHER-193: decryption of patient personal data MUST go through this helper so
 * any row encrypted before or after the FieldSherwood .format("YYYY-MM-DD")
 * change round-trips without collapsing to 1970-01-01.
 */
export function birthdateToDate(value: DateLike): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
        if (isNaN(value.getTime())) return null;
        return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
    }
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const d = new Date(Number(str));
    if (isNaN(d.getTime())) return null;
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function formatBirthdateForDisplay(value: DateLike, localeCode: string): string {
    const date = birthdateToDate(value);
    if (!date) return '';
    return dateToFullDateString(date, localeCode);
}

export function areSameDates(date1: DateLike, date2: DateLike): boolean {
    const d1 = birthdateToDate(date1);
    const d2 = birthdateToDate(date2);
    if (!d1 || !d2) return false;
    return d1.getFullYear() === d2.getFullYear()
        && d1.getMonth() === d2.getMonth()
        && d1.getDate() === d2.getDate();
}

export function yearsFromDate(fromDate: Date | null | undefined): number | null {
    if (!fromDate || typeof (fromDate as Date).getFullYear !== 'function') return null;
    return new Date().getFullYear() - (fromDate as Date).getFullYear();
}

/**
 * SHER-896. Full years elapsed from `fromDate` to `atDate`, month and day aware.
 *
 * A sibling of `yearsFromDate` rather than a replacement for it: that one answers
 * "thisYear - birthYear" and six modules depend on exactly that answer. Two things make it
 * the wrong helper for analytics — it can only ask about today, and it rounds a patient born
 * in December up by a year for eleven months of every year. The demographics panel has to
 * report the age a patient WAS at the appointment being counted, so both have to change.
 *
 * Returns `null` when either date is unparsable, so a caller must decide what an unknown age
 * means. The caller this was written for counts it as an explicit `Unknown` bucket; the code it
 * replaced silently substituted 0, which quietly pulled the average age down towards zero.
 */
export function yearsBetween(fromDate: DateLike, atDate: DateLike): number | null {
    const from = birthdateToDate(fromDate);
    const at = birthdateToDate(atDate);
    if (!from || !at) return null;

    let years = at.getFullYear() - from.getFullYear();
    const birthdayNotReached =
        at.getMonth() < from.getMonth()
        || (at.getMonth() === from.getMonth() && at.getDate() < from.getDate());
    if (birthdayNotReached) years -= 1;

    // A birthdate after the reference date is data we cannot interpret, not an age of -3.
    return years < 0 ? null : years;
}
